<template>
  <transition name="grabber-animation">
    <div
      v-show="grabberActive"
      class="grabber fixed lg:flex w-screen h-screen z-50 top-0 left-0 px-[20px] lg:px-20 bg-[#0000009a]"
    >
      <!-- close desktop -->
      <img
        @click="close"
        class="w-[32px] h-[32px] right-0 mt-[20px] lg:hidden absolute block mr-[40px] hover:rotate-180 hover:duration-500 hover:ease-in hover:transition duration-500 cursor-pointer"
        :src="$filters.media_path('/images/close-mail-grabber.svg')"
        alt="24/7 Online Course Building"
      />
      <!-- heading -->
      <transition name="grabber-animation-inner">
        <div
          v-show="grabberActive"
          class="grabber-inner my-auto lg:h-[100px] lg:w-[350px] w-full rounded-lg pt-[150px] lg:pt-0"
        >
          <h2
            class="text-white text-[34px] md:text-[44px] mx-auto font-semibold text-center"
          >
            Sign up for FREE
          </h2>
          <h3
            class="text-white text-sm md:text-lg mx-auto font-semibold text-center"
          >
            and start using Platforma in seconds!
          </h3>
        </div>
      </transition>
      <!-- form -->
      <transition name="grabber-animation-inner">
        <div
          class="lg:my-auto mx-auto lg:ml-[50px] mt-[60px] bg-white border-4 border-[#55555541] rounded-xl min-[450px]:w-[450px] min-[450px]:h-[78px] h-[74px] p-[6px] align-middle"
        >
          <Form
            @submit="handleSubmit"
            :validation-schema="schema"
            v-slot="{ errors }"
            class="flex my-auto"
          >
            <Field
              as="input"
              type="email"
              name="email"
              id="email"
              class="peer appearance-none h-[56px] w-[100%] rounded-[4px] px-3 py-0 pl-[25px] text-sm placeholder-[#292d34] focus:outline-none sm:w-[360px]"
              placeholder="Enter Your Work Email"
              v-model="formData.email"
            />
            <ErrorMessage
              name="email"
              class="text-red-400 text-xs mt-2 absolute block top-[54%]"
            />
            <!-- actions -->
            <div class="flex">
              <button
                class="transition my-auto h-[44px] w-[125px] min-[450px]:h-[56px] min-[450px]:w-[148px] rounded-lg bg-[#2d8bff] min-[450px]:text-[15px] text-[14px] font-semibold text-white active:bg-[#054ca3] hover:scale-[102%] dark:border-none hover:ease-in hover:transition duration-1000 hover:delay-200"
                type="submit"
                :disabled="isLoading['submit']"
              >
                <LoaderComponent
                  :isReactive="isLoading['submit']"
                ></LoaderComponent>
                {{ isLoading['submit'] ? '' : 'Get Platforma' }}
              </button>
            </div>
          </Form>
        </div>
      </transition>
      <!-- close mobile -->
      <img
        @click="close"
        class="lg:w-[52px] lg:h-[52px] right-0 mt-[20px] hidden absolute lg:block mr-[40px] hover:rotate-180 hover:duration-500 hover:ease-in hover:transition duration-500 cursor-pointer"
        :src="$filters.media_path('/images/close-mail-grabber.svg')"
        alt="24/7 Online Course Building"
      />
    </div>
  </transition>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount } from 'vue';
import { useReCaptcha } from 'vue-recaptcha-v3';
import LoaderComponent from '../../../shareComponents/js/Components/LoaderComponent.vue';
import Axios from 'axios';
import { Form, Field, ErrorMessage } from 'vee-validate';
import * as yup from 'yup';

const emit = defineEmits(['close']);
const formData = reactive({
  email: ''
});
const isLoading = reactive({});
const backEndError = ref('');
const { executeRecaptcha, recaptchaLoaded } = useReCaptcha();

const props = defineProps({
  grabberActive: {
    type: Boolean,
    required: false
  },
  freePlan: {
    type: Boolean,
    required: false,
    default: false
  },
  payPlan: {
    type: Boolean,
    required: false,
    default: false
  },
  planId: {
    type: Number,
    required: false,
    default: null
  }
});

const schema = yup.object({
  email: yup.string().required('Please provide an email').email()
});

const close = () => {
  emit('close');
};

const handleSubmit = async () => {
  isLoading['submit'] = true;
  try {
    const recaptcha_token = await handleRecaptcha();

    const formDataToSubmit = createFormData(formData, recaptcha_token);

    if (props.freePlan) {
      const res = await postFormFree(formDataToSubmit);
      handleResFree(res);
    } else {
      const res = await postFormPay(formDataToSubmit);
      handleResPay(res);
    }
  } catch (err) {
    console.log(err);
    isLoading['submit'] = false;
  }
};

const handleRecaptcha = async () => {
  await recaptchaLoaded();
  const recaptcha_token = await executeRecaptcha('login');
  return recaptcha_token;
};

const createFormData = (dataObject, recaptcha_token) => {
  let formDataToSubmit = new FormData();

  Object.keys(dataObject).forEach((key) => {
    formDataToSubmit.append(key, dataObject[key]);
  });

  formDataToSubmit.append('recaptcha_token', recaptcha_token);

  return formDataToSubmit;
};

const postFormFree = async (formDataToSubmit) => {
  try {
    let response = await Axios({
      method: 'post',
      url: '/free-signup-quick-survey/captureEmail',
      data: formDataToSubmit,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response;
  } catch (error) {
    throw error;
  }
};

const postFormPay = async (formDataToSubmit) => {
  try {
    let response = await Axios({
      method: 'post',
      url: '/checkout/captureEmail',
      data: formDataToSubmit,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response;
  } catch (error) {
    throw error;
  }
};

const handleResFree = (res) => {
  console.log(res);

  if (res.status === 204 || res.status === 200) {
    window.location.href = '/free-signup-quick-survey';
    isLoading['submit'] = false;
  } else {
    backEndError.value = res.data.message;
  }

  isLoading['submit'] = false;
};

const handleResPay = (res) => {
  console.log(res);

  if (res.status === 204 || res.status === 200) {
    window.location.href = `/checkout/payment?plan_id=${props.planId}`;
    isLoading['submit'] = false;
  } else {
    backEndError.value = res.data.message;
  }

  isLoading['submit'] = false;
};
</script>

<style scoped>
.grabber-animation-enter-active,
.grabber-animation-leave-active {
  transition: opacity 0.7s cubic-bezier(0.52, 0.02, 0.19, 1.02);
}

.grabber-animation-enter-from,
.grabber-animation-leave-to {
  opacity: 0;
}

.grabber-animation-inner-enter-active {
  transition: all 0.7s cubic-bezier(0.52, 0.02, 0.19, 1.02) 0.15s;
}

.grabber-animation-inner-leave-active {
  transition: all 0.7s cubic-bezier(0.52, 0.02, 0.19, 1.02);
}

.grabber-animation-inner-enter-from {
  opacity: 0;
  transform: scale (0.8);
}
.grabber-animation-inner-leave-to {
  transform: scale (0.8);
}

/* Extra small devices (phones, 600px and down) */
@media only screen and (max-width: 600px) {
  .grabber-animation-enter-active {
    animation: moveDown 0.4s ease-in;
  }
  @keyframes moveDown {
    0% {
      transform: translateY(-120%);
    }

    100% {
      transform: translateY(0);
    }
  }
}

.grabber {
  background-image: url(../media/images/mail-grabber-stunner-bg.svg);
  position: contain;
  left: 0;
  /* display: block; */
  background-repeat: no-repeat;
}
</style>
